import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useUnreadNotificationCount } from '../hooks/queries/useNotificationsQueries';
import { fetchLatestUnreadNotification } from '../api/notifications';
import useFollowBack from '../hooks/useFollowBack';
import { TOAST_TYPES, describeNotification } from '../utils/notificationCopy';
import { NOTIFICATION_TYPES, getNotificationTarget } from '../utils/notifications';
import { NotificationMark, NotificationText } from './Notifications';
import Button from './ui/Button';

/**
 * The important-update toast (operator, 2026-10-04, from "Notifications C"): someone followed you or you're in a
 * group. One row, never a stack, over the page rather than in it, and it leaves on its own. Tap a follow: their
 * profile; tap a group update: that group; Follow back: follows; X: gone.
 *
 * Never a barrage (operator, 2026-10-04): only the newest one, only if it arrived after the last toast and within
 * a day, and at most one toast every ten minutes. Coming back to a pile of follows shows one, not the pile.
 */
const SHOW_MS = 7000;
const MAX_AGE_MS = 24 * 3600 * 1000;
const COOLDOWN_MS = 10 * 60 * 1000;
/** You are in a group: always said, never held by the cooldown (2026-10-27 latency audit - an RSVP then a group join
 * inside ten minutes lost the join's toast, the one that matters). */
const ALWAYS_SHOW = new Set(['join_approved', 'queue_matched']);
const SEEN_KEY = 'prii.notifToast.seen';
const LAST_KEY = 'prii.notifToast.lastAt';

function readLast() {
  try {
    return Number(window.localStorage.getItem(LAST_KEY)) || 0;
  } catch {
    return 0;
  }
}
function writeLast(createdAt) {
  try {
    window.localStorage.setItem(
      LAST_KEY,
      String(Math.max(Date.now(), new Date(createdAt).getTime())),
    );
  } catch {
    /* private mode */
  }
}
const TYPES = [...TOAST_TYPES];

function readSeen() {
  try {
    return JSON.parse(window.localStorage.getItem(SEEN_KEY) || '[]');
  } catch {
    return [];
  }
}
function markSeen(id) {
  try {
    window.localStorage.setItem(
      SEEN_KEY,
      JSON.stringify([id, ...readSeen().filter((x) => x !== id)].slice(0, 50)),
    );
  } catch {
    /* private mode */
  }
}

// Dev only: ?toast=follow|group shows a sample so the layout can be checked without a second account.
function devSample() {
  if (!import.meta.env.DEV) return null;
  const kind = new URLSearchParams(window.location.search).get('toast');
  if (!kind) return null;
  const actor = { id: 'dev-actor', display_name: 'Maya', profile_photo_url: null };
  return kind === 'group'
    ? {
        id: 'dev-group',
        type: NOTIFICATION_TYPES.JOIN_APPROVED,
        group: { id: 'g', name: 'Portola group' },
        created_at: new Date().toISOString(),
      }
    : {
        id: 'dev-follow',
        type: NOTIFICATION_TYPES.FOLLOW,
        actor,
        created_at: new Date().toISOString(),
      };
}

export default function NotificationToast() {
  const { session } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isFollowing, followBack } = useFollowBack();
  const { data: unread = 0 } = useUnreadNotificationCount(userId);
  // Keyed on the unread count, which the header's realtime channel invalidates on every insert.
  const { data: latest } = useQuery({
    queryKey: ['notifications', 'toast', userId, unread],
    queryFn: () => fetchLatestUnreadNotification(userId, TYPES),
    enabled: Boolean(userId) && unread > 0,
    staleTime: Infinity,
  });
  const [shown, setShown] = useState(null);

  useEffect(() => {
    const candidate = devSample() ?? latest;
    if (!candidate || pathname === '/notifications') return;
    if (!devSample()) {
      const created = new Date(candidate.created_at).getTime();
      const last = readLast();
      if (readSeen().includes(candidate.id)) return;
      if (Date.now() - created > MAX_AGE_MS) return;
      // Older than the last toast (a backlog), or too soon after it: stay quiet. The bell's count still says it.
      if (
        created <= last ||
        (!ALWAYS_SHOW.has(candidate.type) && Date.now() - last < COOLDOWN_MS)
      ) {
        markSeen(candidate.id);
        return;
      }
      writeLast(candidate.created_at);
    }
    markSeen(candidate.id);
    setShown(candidate);
  }, [latest, pathname]);

  useEffect(() => {
    if (!shown || devSample()) return undefined;
    const t = window.setTimeout(() => setShown(null), SHOW_MS);
    return () => window.clearTimeout(t);
  }, [shown]);

  useEffect(() => {
    if (pathname === '/notifications') setShown(null);
  }, [pathname]);

  if (!shown) return null;
  const { kind, parts } = describeNotification(shown);
  const actorId = shown.actor?.id;
  const canFollowBack =
    shown.type === NOTIFICATION_TYPES.FOLLOW && actorId && !isFollowing(actorId);

  return (
    <div className="notif-toast-wrap" role="status" aria-live="polite">
      <div className="notif-toast">
        <NotificationMark notification={shown} kind={kind} size="xs" />
        <button
          type="button"
          className="notif-toast__text"
          onClick={() => {
            setShown(null);
            navigate(getNotificationTarget(shown) ?? '/notifications');
          }}
        >
          <NotificationText parts={parts} />
        </button>
        {canFollowBack && (
          <Button
            type="button"
            size="sm"
            className="shrink-0 !rounded-full !px-2.5"
            onClick={() => {
              void followBack(actorId);
              setShown(null);
            }}
          >
            Follow back
          </Button>
        )}
        <button
          type="button"
          className="notif-toast__close"
          aria-label="Dismiss"
          onClick={() => setShown(null)}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
